import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

// EXIT_MS must match the transition-duration below — it's how long the modal
// stays mounted after `open` goes false so the fade/scale-out can finish
// instead of the dialog just vanishing mid-animation.
const EXIT_MS = 180;

const Modal = ({ open, onClose, title, children }) => {
  // Mounted for one extra frame beyond `open` so the enter transition has a
  // true "before" state to animate from, and for EXIT_MS beyond `open`
  // turning false so the exit transition can play before unmount.
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const timeout = setTimeout(() => setRendered(false), EXIT_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!rendered) return null;

  // Portaled so no ancestor transform/filter (e.g. the route fade-in wrapper)
  // can re-anchor the fixed backdrop away from the viewport.
  return createPortal(
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-[180ms] ${visible ? 'opacity-100' : 'opacity-0'}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bg-white rounded-lg shadow-lg p-6 w-full max-w-md transition-all duration-[180ms] ease-out ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
      >
        <div className="flex justify-between items-start mb-4">
          {title && <h3 className="text-lg font-semibold">{title}</h3>}
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-auto p-1.5 -mt-1 -mr-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-ring flex-shrink-0"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
